/* Web-only responsive layout shared by boats/[id], tours/[id], events/[id] and
   articles/[slug]. Mirrors src/constants/theme.ts (Spacing.three=16, four=24,
   five=32; Radius['2xl']=20; MaxContentWidth=1280; SidebarWidth=256) and
   src/hooks/use-breakpoint.ts (Breakpoints.lg=1024).

   The breakpoint is a CSS media query — the same first-paint approach used by
   filters-bar.module.css and home.module.css — instead of a post-mount JS
   `isLg` swap. Theme-dependent colors stay inline; structure lives here.

   One DOM serves both breakpoints, so the title, hero image, CTA and
   description/body ship once in the static HTML instead of twice. The two
   layouts group the same items differently — mobile wraps title/meta/price/
   CTA/body in ONE card overlapping a full-bleed hero, desktop splits them into
   a header, a separate sticky booking card and an about column (or, for
   articles, a single reading column) — which grid-template-areas alone can't
   express. `display: contents` does it: it drops an element's own box and
   promotes its children into the parent's layout.

     .detailCard     mobile: the overlapping card   desktop: contents, so its
                                                     children are grid items of
                                                     .detail / .articleDetail
     .detailBooking  mobile: contents (no nested     desktop: the sticky card
                     card inside the card)

   A `display: contents` element paints no box, so the theme colors can stay
   inline on both elements — they only show at the breakpoint where that
   element actually renders a box. Only apply it to plain <div>s: older
   Safari/Chrome dropped the semantic role (landmark, heading) of an element
   with `display: contents`. */

/* Small mobile-only bits of the card (e.g. the divider between the CTA and
   the body, which on desktop live in different columns). */
.mobileOnly {
  display: block;
}

.shell {
  box-sizing: border-box;
  width: 100%;
}

.shellSidebar {
  display: none;
}

.shellMain {
  min-width: 0;
}

.detailMedia {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
}

.heroImg {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Low-res source images (e.g. AWIN/Fnac feed thumbnails, ~222x222) get
   letterboxed instead of stretched full-bleed — see Hero() in
   events/[id].web.tsx. */
.heroImgContain {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* A control pinned to the hero's bottom-right corner (the events save
   button). On mobile the card covers the hero's bottom 24px, so it sits
   24px higher there. */
.heroCornerBtn {
  position: absolute;
  right: 16px;
  bottom: 40px;
}

/* Mobile: the content card overlapping the hero's lower edge.
   - position: relative lifts it above the hero: without it the <img> paints
     over the card's top 24px and the rounded corners never show.
   - flex column (here and on desktop's .detailBooking) because the Button's
     `fullWidth` is `alignSelf: stretch`, a no-op in a block parent; it also
     blockifies bare react-native-web <Text>s (inline by default), so their
     marginTop applies. */
.detailCard {
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: -24px;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-style: solid;
  border-width: 1px;
  border-bottom-width: 0;
  padding: 24px 16px 32px;
}

.detailTitle {
  margin: 0;
  font-weight: 700;
  font-size: 26px;
  line-height: 33px;
}

.detailBooking {
  display: contents;
}

.articleBody {
  max-width: 720px;
}

@media (min-width: 1024px) {
  .mobileOnly {
    display: none;
  }

  .heroCornerBtn {
    bottom: 16px;
  }

  /* Page shell: filters sidebar + main content, centered like the home grid. */
  .shell {
    display: grid;
    grid-template-columns: 256px 1fr;
    gap: 32px;
    max-width: 1280px;
    margin: 0 auto;
    padding: 24px;
  }

  .shellSidebar {
    display: block;
    min-width: 0;
  }

  /* Listings (boats, tours, events): title + meta lead (top-left), the
     price/CTA card top-right, image below the heading, description under the
     image. Surfaces the text and CTA above the fold instead of a full-bleed
     image. */
  .detail {
    display: grid;
    grid-template-columns: 1fr 340px;
    grid-template-areas:
      'header  booking'
      'media   booking'
      'about   booking';
    column-gap: 32px;
    row-gap: 24px;
    align-items: start;
  }

  /* Articles: no action card, so a single readable column where the title
     leads and the body stays a comfortable measure. */
  .articleDetail {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'header'
      'media'
      'about';
    row-gap: 24px;
    max-width: 760px;
  }

  .detailMedia {
    grid-area: media;
    border-radius: 20px;
    overflow: hidden;
  }

  .detailCard {
    display: contents;
  }

  .detailHeader {
    grid-area: header;
    min-width: 0;
  }

  .detailBooking {
    display: flex;
    flex-direction: column;
    grid-area: booking;
    position: sticky;
    top: 24px;
    box-sizing: border-box;
    border-style: solid;
    border-width: 1px;
    border-radius: 20px;
    padding: 24px;
  }

  .detailAbout {
    grid-area: about;
    min-width: 0;
    max-width: 680px;
  }

  .articleBody {
    grid-area: about;
    min-width: 0;
  }

  .detailTitle {
    font-size: 34px;
    line-height: 41px;
  }
}
